import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DemiLightText, RegularText } from '../lib/Text';

type TooltipProps = {
    title?: string;
    subTitle?: string;
    fontSize?: number;
    lineHeight?: number;
    color?: string;
    letterSpacing?: number;
    fontType?: 'regular' | 'demilight';
    customContent?: React.ReactNode;
    width?: number; // 추가: 툴팁 너비
    height?: number; // 추가: 툴팁 높이
    px?: number; // 추가: 툴팁 패딩 X
    py?: number; // 추가: 툴팁 패딩 Y
    opacity?: number; // 추가: 툴팁 불투명도
    bgColor?: string; // 추가: 툴팁 배경색
    borderRadius?: number; // 추가: 툴팁 테두리 반경
};

type Props = {
    tooltip: TooltipProps;
    children: React.ReactNode;
    className?: string;
    disabled?: boolean;
};

export const TooltipWrapper = ({ tooltip, children, className, disabled = false }: Props) => {
    const [isHovered, setIsHovered] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const triggerRef = useRef<HTMLDivElement>(null);

    const {
        title,
        subTitle,
        fontSize = 13,
        lineHeight = 16,
        color = 'white',
        letterSpacing = -0.3,
        fontType = 'regular',
        customContent,
        width, // 기본 너비
        height, // 기본 높이
        px = 14, // 기본 패딩 X
        py = 16, // 기본 패딩 Y
        opacity = 0.9, // 기본 불투명도
        bgColor = '#36393E', // 기본 배경색
        borderRadius = 9, // 기본 테두리 반경
    } = tooltip;

    const TextComponent = fontType === 'demilight' ? DemiLightText : RegularText;

    useEffect(() => {
        if (isHovered && triggerRef.current && !disabled) {
            const rect = triggerRef.current.getBoundingClientRect();
            setPosition({
                top: rect.top + window.scrollY - 10, // 위로 10px
                left: rect.left + window.scrollX + rect.width / 2,
            });
        }
    }, [isHovered, disabled]);

    return (
        <div
            className={`relative inline-block z-30 ${className}`}
            ref={triggerRef}
            onMouseEnter={() => !disabled && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {children}

            {isHovered &&
                !disabled &&
                createPortal(
                    <div
                        className="absolute z-[9999] text-white shadow-lg  text-start"
                        style={{
                            top: `${position.top}px`,
                            left: `${position.left}px`,
                            transform: 'translate(-50%, -100%)',
                            position: 'absolute',
                            width: `${width}px`,
                            height: `${height}px`,
                            paddingRight: `${px}px`,
                            paddingLeft: `${px}px`,
                            paddingTop: `${py}px`,
                            paddingBottom: `${py}px`,
                            opacity: opacity,
                            backgroundColor: bgColor,
                            borderRadius: `${borderRadius}px`,
                        }}
                    >
                        {customContent ? (
                            customContent
                        ) : (
                            <>
                                <TextComponent
                                    fontSize={fontSize}
                                    lineHeight={lineHeight}
                                    color={color}
                                    letterSpacing={letterSpacing}
                                >
                                    {title}
                                </TextComponent>
                                {subTitle && (
                                    <>
                                        <br />
                                        <br />
                                        <RegularText
                                            fontSize={fontSize}
                                            lineHeight={lineHeight}
                                            color={color}
                                            letterSpacing={letterSpacing}
                                        >
                                            {subTitle}
                                        </RegularText>
                                    </>
                                )}
                            </>
                        )}
                    </div>,
                    document.body // ✅ Portal 렌더링 위치
                )}
        </div>
    );
};
